import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

/**
 * PaywallGate — global listener for `crescendo:paywall` events dispatched
 * by the axios interceptor when the backend returns 402. Surfaces a
 * friendly upgrade prompt that links to /pricing instead of a raw error
 * toast.
 *
 * The event detail mirrors the backend `HTTPException.detail` payload:
 *   { error, resource?, feature?, current?, limit?, plan, message }
 */
const RESOURCE_LABELS = {
  workspaces: "workspace",
  documents: "document",
  templates: "template",
  tabs: "tab",
  key_pairs: "key-value pair",
  resources_per_node: "resource",
};

const NEXT_PLAN = {
  free: "Standard",
  standard: "Pro",
  pro: "Business",
  business: "Business",
};

function pluralize(singular, n) {
  if (!singular) return "";
  if (n === 1) return singular;
  // Very simple pluralization — our resource names are simple nouns.
  return singular.endsWith("y") ? singular.slice(0, -1) + "ies" : singular + "s";
}

export function PaywallGate() {
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onPaywall = (e) => setDetail(e.detail || {});
    window.addEventListener("crescendo:paywall", onPaywall);
    return () => window.removeEventListener("crescendo:paywall", onPaywall);
  }, []);

  if (!detail) return null;

  const plan = detail.plan || "free";
  const next = NEXT_PLAN[plan] || "Pro";
  const isLimit = detail.error === "plan_limit_reached";
  const resourceSingular = RESOURCE_LABELS[detail.resource] || detail.resource;
  const featureLabel = (detail.feature || "").replace(/_/g, " ");

  const title = isLimit ? `${pluralize(resourceSingular, 2)[0]?.toUpperCase()}${pluralize(resourceSingular, 2).slice(1)} limit reached` : "Upgrade required";

  return (
    <div
      className="fixed inset-0 z-[400] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setDetail(null)}
      data-testid="paywall-gate-overlay"
    >
      <div
        className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-gate-modal"
      >
        <button
          type="button"
          onClick={() => setDetail(null)}
          className="absolute top-3 right-3 p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"
          data-testid="paywall-gate-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#5A90F5]/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#5A90F5]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#A1A1AA]">Upgrade</div>
            <div className="text-base font-semibold text-[#EDEDED]" data-testid="paywall-gate-title">{title}</div>
          </div>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-4" data-testid="paywall-gate-message">
          {detail.message || (isLimit
            ? `You've hit the ${pluralize(resourceSingular || "item", detail.limit || 2)} cap on your ${plan} plan.`
            : featureLabel
              ? `${featureLabel.charAt(0).toUpperCase()}${featureLabel.slice(1)} isn't included in your ${plan} plan.`
              : `This feature isn't included in your ${plan} plan.`)}
        </p>
        {isLimit && typeof detail.limit === "number" && (
          <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md p-3 mb-4 text-xs text-[#A1A1AA]">
            <div className="flex justify-between mb-1">
              <span>Current usage</span>
              <span className="text-[#EDEDED] font-mono">{detail.current} / {detail.limit}</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#2A2A2A] overflow-hidden">
              <div className="h-full bg-[#5A90F5]" style={{ width: "100%" }} />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => setDetail(null)}
            className="text-xs px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:bg-[#1a1a1a]"
            data-testid="paywall-gate-dismiss"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => { setDetail(null); navigate("/pricing"); }}
            className="text-xs px-3 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a7fe0] inline-flex items-center gap-1.5"
            data-testid="paywall-gate-upgrade"
          >
            <Sparkles className="w-3 h-3" />
            Upgrade to {next}
          </button>
        </div>
      </div>
    </div>
  );
}
